import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@bice_vida/ds-components-button';
import { CheckIcon, CircleCheckIcon, CircleIcon } from '@components/Icons';
import { PlanT } from '@segurodeceso/core/domain/plans';
import { currencyFormat } from '@segurodeceso/core/utils/currency';
import { useCurrencyStore } from '@stores';

interface PlanCardPropsI {
  plan: PlanT;
  isSelected?: boolean;
  onCardClick?: () => void;
  onMoreDetails?: () => void;
}

const PlanCard = ({
  plan,
  isSelected = false,
  onCardClick,
  onMoreDetails,
}: PlanCardPropsI): ReactElement => {
  const { t } = useTranslation();
  const border = isSelected ? 'border-[2px] border-[#1F61F7]' : 'border-[2px] border-[#AFBACC]';
  const UFValue = useCurrencyStore().currency.UFValue;

  return (
    <div
      className={`flex flex-col justify-between gap-8 w-[252px] min-h-[528px] ${border} rounded-lg px-6 pb-6 pt-4 relative cursor-pointer`}
      onClick={onCardClick}
    >
      <div className="absolute top-1 right-2">
        {isSelected ? <CircleCheckIcon /> : <CircleIcon />}
      </div>
      <div className="flex flex-col justify-between gap-8">
        <div className="flex flex-1">
          <p className="font-bold text-neutral/darkgrey/90 text-lg">{plan.planName}</p>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <p className="font-normal text-color/neutral/80 text-xs">{t('plan_card.from')}</p>
          <div className="flex flex-1 items-center">
            <p className="font-extrabold text-neutral/darkgrey/90 text-[32px]">
              {currencyFormat(plan.prices.price * UFValue, 'CLP')}
            </p>
            <p className="font-normal text-neutral/darkgrey/90 text-[13px] leading-4 pl-1 pt-1">
              / {currencyFormat(plan.prices.price, 'UF')}
            </p>
          </div>
          <p className="font-normal text-color/neutral/80 text-xs">{t('plan_card.per_month')}</p>
        </div>
      </div>
      <div className="flex flex-1">
        <div className="flex flex-col gap-2">
          <p className="font-bold text-neutral/darkgrey/90 text-paragraph/s paragraph/s">
            {t('plan_card.main_coverages')}
          </p>
          {plan.coverages.map((coverage, index) => {
            if (
              coverage.type === 'funeralServices' ||
              coverage.type === 'burialOrCremation' ||
              coverage.type === 'accidentalDeath'
            ) {
              return (
                <div key={index} className="flex flex-row gap-2">
                  <div className="pt-1.5">
                    <CheckIcon />
                  </div>
                  <p className="font-normal text-color/neutral/80 text-paragraph/s paragraph/s inline-flex items-end">
                    <Trans
                      t={t}
                      i18nKey={`plan_card.until_${coverage.type}`}
                      values={{
                        clp: currencyFormat(coverage.amount * UFValue, 'CLP'),
                        uf: currencyFormat(coverage.amount, 'UF'),
                      }}
                    />
                  </p>
                </div>
              );
            }
          })}
        </div>
      </div>
      <div className="flex justify-center items-center">
        <Button
          variant="link"
          size="m"
          className="w-full font-medium"
          onClick={(e) => {
            e.stopPropagation();
            onMoreDetails();
          }}
          style={{ padding: 0, fontWeight: 500 }}
        >
          {t('plan_card.more_details')}
        </Button>
      </div>
    </div>
  );
};

export default PlanCard;
